import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, TextInput, FlatList, ActivityIndicator, Image, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { apiService } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

export interface ReportContactOption {
  contactId: string;
  name: string;
  phone?: string;
  avatar?: string;
}

export interface ReportContactSelection {
  /** Omni-contact id (OmniContactModel._id) — the id space Reports' contact filter
   * expects everywhere (see report-message-filter.entity.ts's doc comment on
   * ReportMessageFilter.contactId for why this specific id space was chosen). */
  id: string;
  label?: string;
}

export interface ReportContactPickerProps {
  /** Selected contacts (multi-select); empty = all contacts. */
  value: ReportContactSelection[];
  onChange: (contacts: ReportContactSelection[]) => void;
}

const DEBOUNCE_MS = 300;

/**
 * Searchable contact picker for the Reports screen's contact filter — replaces the
 * old free-text "type a contactId" input, which asked users to know and type an
 * opaque database id by hand. Backed by the same omni-contact list/search endpoint
 * (apiService.getParentContacts) the Contacts screen itself uses, so results are real
 * business contacts by name/phone. Named distinctly from the existing
 * components/ContactSelector.tsx (a much heavier order/booking-flow component with an
 * entirely different mode/variant/contact/onContactSelect API already used in several
 * screens) rather than reusing that name, to avoid colliding with it. Modal-based
 * (portal-mounts at the document root on web) for the same reason every other picker
 * on ReportsScreen is — avoids the stacking-context bug an inline absolute-positioned
 * dropdown had before it was converted to a Modal.
 */
export const ReportContactPicker: React.FC<ReportContactPickerProps> = ({ value, onChange }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ReportContactOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (text: string) => {
    setLoading(true);
    try {
      const res = await apiService.getParentContacts({ name: text || undefined, limit: 25 });
      const raw: any[] = (res.data as any)?.contacts || (Array.isArray(res.data) ? res.data : []) || [];
      setResults(raw.map((c: any) => ({
        contactId: String(c._id),
        name: c.profile?.name || c.name || c.profile?.phone || c.phone || '',
        phone: c.profile?.phone || c.phone,
        avatar: c.profile?.avatar || c.avatar,
      })));
    } catch (err) {
      console.warn('[ReportContactPicker] Failed to search contacts:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), query ? DEBOUNCE_MS : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, visible, search]);

  const open = () => {
    setQuery('');
    setVisible(true);
  };

  const isSelected = (contactId: string) => value.some((c) => c.id === contactId);

  // Tapping toggles; the list stays open so several contacts can be picked.
  const toggle = (contact: ReportContactOption) => {
    onChange(isSelected(contact.contactId)
      ? value.filter((c) => c.id !== contact.contactId)
      : [...value, { id: contact.contactId, label: contact.name }]);
  };

  const clear = () => onChange([]);

  const triggerText = value.length === 0
    ? t('reports.filters.allContacts')
    : value.length === 1
      ? (value[0].label || value[0].id)
      : t('reports.filters.contactsSelected', { count: value.length });

  return (
    <>
      <TouchableOpacity style={[styles.trigger, { flexDirection: rtl.rowDirection }]} onPress={open}>
        <Ionicons name="person-circle-outline" size={14} color={colors.text.secondary as string} />
        <Text style={styles.triggerText} numberOfLines={1}>{triggerText}</Text>
        {value.length > 0 ? (
          <TouchableOpacity onPress={clear} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="close-circle" size={14} color={colors.text.disabled as string} />
          </TouchableOpacity>
        ) : (
          <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
        )}
      </TouchableOpacity>

      <Modal visible={visible} transparent animationType="fade" onRequestClose={() => setVisible(false)}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setVisible(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.card} onPress={() => {}}>
            <Text style={styles.title}>{t('reports.filters.contact')}</Text>
            <TextInput
              style={styles.searchInput}
              placeholder={t('reports.filters.searchContacts')}
              placeholderTextColor={colors.text.disabled as string}
              value={query}
              onChangeText={setQuery}
              autoFocus={Platform.OS === 'web'}
              autoCapitalize="none"
            />
            {loading ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={colors.primary as string} />
              </View>
            ) : (
              <FlatList
                data={results}
                keyExtractor={(c) => c.contactId}
                style={styles.list}
                keyboardShouldPersistTaps="handled"
                ListEmptyComponent={<Text style={styles.emptyText}>{t('reports.filters.noContactsFound')}</Text>}
                renderItem={({ item }) => (
                  <TouchableOpacity style={[styles.option, { flexDirection: rtl.rowDirection }]} onPress={() => toggle(item)}>
                    {item.avatar ? (
                      <Image source={{ uri: item.avatar }} style={styles.avatar} />
                    ) : (
                      <View style={styles.avatarFallback}>
                        <Text style={styles.avatarFallbackText}>{(item.name || '?').trim().charAt(0).toUpperCase()}</Text>
                      </View>
                    )}
                    <View style={styles.optionText}>
                      <Text style={styles.optionName} numberOfLines={1}>{item.name}</Text>
                      {!!item.phone && <Text style={styles.optionPhone} numberOfLines={1}>{item.phone}</Text>}
                    </View>
                    <Ionicons name={isSelected(item.contactId) ? 'checkbox' : 'square-outline'} size={20} color={colors.primary as string} />
                  </TouchableOpacity>
                )}
              />
            )}
            <View style={[styles.footer, { flexDirection: rtl.rowDirection }]}>
              {value.length > 0 && (
                <TouchableOpacity onPress={clear} style={styles.footerSecondary}>
                  <Text style={styles.footerSecondaryText}>{t('reports.filters.clearSelection')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity onPress={() => setVisible(false)} style={styles.footerPrimary}>
                <Text style={styles.footerPrimaryText}>
                  {value.length > 0 ? t('reports.filters.doneCount', { count: value.length }) : t('reports.filters.done')}
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const getStyles = () => StyleSheet.create({
  footer: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  footerPrimary: {
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  footerPrimaryText: {
    color: colors.text.onPrimary,
    fontWeight: '600',
  },
  footerSecondary: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  footerSecondaryText: {
    color: colors.text.secondary,
  },
  trigger: {
    alignItems: 'center',
    gap: spacing.xs,
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    backgroundColor: colors.background.secondary as string,
    maxWidth: 180,
  },
  triggerText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
    flexShrink: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: '90%',
    maxWidth: 380,
    maxHeight: '75%',
    backgroundColor: colors.background.surface as string,
    borderRadius: 14,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
  },
  searchInput: {
    height: 40,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.background.secondary as string,
    color: colors.text.primary as string,
    fontSize: typography.fontSize.sm,
  },
  loadingBox: {
    paddingVertical: spacing.lg,
    alignItems: 'center',
  },
  list: {
    maxHeight: 360,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    textAlign: 'center',
    paddingVertical: spacing.lg,
  },
  option: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  avatarFallback: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: withAlpha(colors.primary, 0.13),
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    fontSize: 13,
    fontWeight: '700' as const,
    color: colors.primary as string,
  },
  optionText: { flex: 1, minWidth: 0 },
  optionName: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  optionPhone: {
    fontSize: 11,
    color: colors.text.secondary as string,
    marginTop: 1,
  },
});
